// Bugsnag
import Bugsnag from "@bugsnag/js";
import BugsnagPluginVue from "@bugsnag/plugin-vue";
import { createPinia } from "pinia";
import * as Vue from "vue";
import { reactive } from "vue";

import { warn } from "@/helpers/consoleColors";
import { isChooseNewDeviceStepAvailableKey, shadowRootKey, WIDGET_STYLE_ID } from "@/keys/settings";
import styles from "@/scss/global.scss?inline";

import TIW from "./TradeInWidget.vue";

const pinia = createPinia();

class TIWidget {
    #default_settings = {
        locale: "ru",
        domain: "",
        root_element: "TIWidget",
        mode: "popup", // "popup" | "embed"
    };

    #default_api = {
        onInit: null,
        onLoad: null,
        onShow: null,
        onHide: null,
        onCalc: null,
        onCart: null,
        onDrop: null,
        tradeInWithGood: null,
    };

    current_settings = {};
    current_api = {};

    changeLanguage(newLocale) {
        this.current_settings.locale = newLocale;
    }

    #init_widget(with_settings) {
        this.current_settings = reactive({
            ...this.#default_settings,
            ...with_settings.settings,
        });

        this.current_api = { ...this.#default_api, ...with_settings.api };

        Bugsnag.start({
            apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
            plugins: [new BugsnagPluginVue(Vue)],
            autoDetectErrors: false,
        });

        const bugsnagVue = Bugsnag.getPlugin("vue");
        const app = Vue.createApp(TIW, this.current_settings);

        const hostElement = document.getElementById(this.current_settings.root_element);
        if (!hostElement) return;

        const shadowRoot = hostElement.shadowRoot || hostElement.attachShadow({ mode: "open" });
        shadowRoot.innerHTML = "";

        const vueMountPoint = document.createElement("div");
        vueMountPoint.id = "tiw-app-root";
        shadowRoot.append(vueMountPoint);

        if (styles) {
            const globalStyleTag = document.createElement("style");
            globalStyleTag.textContent = styles;
            shadowRoot.append(globalStyleTag);
        }

        const tiwBundleStyle = document.getElementById(WIDGET_STYLE_ID);
        if (tiwBundleStyle) {
            shadowRoot.append(tiwBundleStyle.cloneNode(true));
        }

        if (import.meta.env.DEV) {
            const hostHead = document.head;

            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.tagName === "STYLE" && node.hasAttribute("data-vite-dev-id")) {
                            shadowRoot.append(node.cloneNode(true));
                        }
                    });
                });
            });

            observer.observe(hostHead, { childList: true });

            hostHead.querySelectorAll("style[data-vite-dev-id]").forEach((style) => {
                shadowRoot.append(style.cloneNode(true));
            });
        }

        app.use(pinia);
        app.use(bugsnagVue);

        app.provide("widget", this);
        app.provide("appInstance", app);
        app.provide(shadowRootKey, shadowRoot);
        app.provide(
            isChooseNewDeviceStepAvailableKey,
            import.meta.env.VITE_APP_IS_CHOOSE_DEVICE_STEP_AVAILABLE === "true",
        );

        app.mount(vueMountPoint);
    }

    constructor(init_settings) {
        const {
            settings: { root_element = "", mode = "" },
        } = init_settings;
        const cacheKey = root_element && mode ? `${root_element}-${mode}` : "";

        if (cacheKey) {
            if (TIWidget.WIDGET_INSTANCE[cacheKey]) {
                console.log("%c[TIWidget] -> RETURN INITIALIZED INSTANCE FROM CACHE", warn);
                return TIWidget.WIDGET_INSTANCE[cacheKey];
            }

            TIWidget.WIDGET_INSTANCE[cacheKey] = this;
        }

        this.#init_widget(init_settings);
    }

    static WIDGET_INSTANCE = {};
}

window.TIWidget = TIWidget;
